/**
 * Configurazione OIDC del portale (TD-004 — login SSO di sviluppo).
 *
 * Le variabili sono iniettate a build-time da Vite (ADR-0019). Se `VITE_OIDC_ISSUER`
 * non è valorizzata, l'SSO è considerato disattivato: la LoginPage mostra solo il
 * form "accesso manuale (dev)" (incolla JWT), come nelle slice precedenti.
 *
 * Endpoint OIDC: costruiti concatenando path FISSI (`/oauth2/authorize`, `/oauth2/token`)
 * all'issuer, SENZA interrogare il documento di discovery
 * (`/.well-known/openid-configuration`). Per il provider mock (e per la maggior parte
 * degli IdP standard) questi path sono stabili: evitiamo una fetch extra e la relativa
 * gestione di errori per restare semplici in questa slice.
 */

/** Issuer OIDC (es. http://localhost:9400 per oidc-mock). undefined → SSO disattivato. */
/**
 * Sentinel per il login SSO "same-origin" (dev via tunnel ngrok): quando VITE_OIDC_ISSUER vale
 * questo valore, l'issuer usato dal BROWSER è l'origine corrente (window.location.origin). Così
 * gli endpoint /oauth2/* sono richiesti sullo STESSO host del portale e instradati dal proxy
 * (vite dev / nginx) verso oidc-mock: il flusso funziona con QUALSIASI URL pubblico (localhost o
 * un dominio ngrok effimero) senza riconfigurare nulla. Il claim `iss` del token resta
 * http://oidc-mock:9400 (timbrato dal mock dietro il proxy con changeOrigin) e i servizi lo
 * verificano con AUTH_JWT_ISSUER costante.
 */
const SAME_ORIGIN_SENTINEL = '__SAME_ORIGIN__';

export function resolveIssuer(raw: string | undefined): string | undefined {
  if (raw === SAME_ORIGIN_SENTINEL) {
    return typeof window !== 'undefined' ? window.location.origin : undefined;
  }
  return raw;
}

/** Issuer OIDC (es. http://localhost:9400 per oidc-mock). undefined → SSO disattivato. */
export const OIDC_ISSUER: string | undefined = resolveIssuer(import.meta.env['VITE_OIDC_ISSUER']);

/** Client ID pubblico registrato sul provider OIDC (default: tenaciousc). */
export const OIDC_CLIENT_ID: string = import.meta.env['VITE_OIDC_CLIENT_ID'] ?? 'tenaciousc';

/** True se l'SSO OIDC è configurato (issuer presente). */
export const isOidcEnabled = (): boolean => Boolean(OIDC_ISSUER);

/** Endpoint di autorizzazione (redirect del browser per il login). */
export function authorizeEndpoint(issuer: string): string {
  return `${issuer}/oauth2/authorize`;
}

/** Endpoint di token exchange (scambio code + verifier → token). */
export function tokenEndpoint(issuer: string): string {
  return `${issuer}/oauth2/token`;
}

/** Redirect URI registrata: pagina di callback nel portale stesso. */
export function redirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}
